'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import RoleCard from './RoleCard';
import LoginFooter from './LoginFooter';
import { Shield, Sun, Moon } from 'lucide-react';

export default function PlatformLoginSelectorContent() {
  const [isDark, setIsDark] = useState(false);
  const [year, setYear] = useState('');

  useEffect(() => {
    setYear(new Date().getFullYear().toString());
    if (localStorage.getItem('aac-platform-theme') === 'dark') {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDark = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('aac-platform-theme', next ? 'dark' : 'light');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="w-full px-6 py-4 flex items-center justify-between border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <AppLogo src="/assets/images/AAC_Logo-1787938885834.jpg" size={36} />
          <div>
            <span className="font-700 text-base text-foreground leading-tight block">API App Cloud</span>
            <span className="text-[11px] text-muted-foreground leading-tight">Enterprise Business Platform</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 badge-success px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-success pulse-dot" />
            <span className="text-[11px] font-500">All systems operational</span>
          </div>
          <button
            onClick={toggleDark}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 badge-primary px-3 py-1.5 rounded-full mb-4">
              <Shield size={12} />
              <span className="text-[11px] font-600">Secure Enterprise Login · login.apiappcloud.com</span>
            </div>
            <h1 className="text-3xl font-700 text-foreground mb-2">Welcome to API App Cloud</h1>
            <p className="text-muted-foreground text-base max-w-md mx-auto">
              Select your account type to continue. Customers use one global AAC identity across organizations.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <RoleCard
              id="role-owner"
              role="Owner"
              subtitle="Organization Owner"
              description="Manage your organization, subscription, billing, applications, employees and business settings."
              href="/platform-login-selector/owner-login"
              iconName="BuildingOffice2Icon"
              accentColor="primary"
              features={['Organization management', 'Subscription & billing', 'App marketplace', 'Full admin access']}
            />
            <RoleCard
              id="role-employee"
              role="Employee"
              subtitle="Licensed App User"
              description="Access assigned business applications, tasks, attendance, CRM records and work modules."
              href="/employee-login"
              iconName="UserIcon"
              accentColor="success"
              features={['Assigned applications', 'Tasks & attendance', 'CRM & projects', 'Team collaboration']}
            />
            <RoleCard
              id="role-orgadmin"
              role="Org Admin"
              subtitle="Organization Administrator"
              description="Manage employees, departments, roles, permissions, workflows and organization operations."
              href="/org-admin-login"
              iconName="UsersIcon"
              accentColor="warning"
              features={['Employee management', 'Roles & permissions', 'Department setup', 'Operations oversight']}
            />
            <RoleCard
              id="role-customer"
              role="Customer"
              subtitle="Global AAC Customer"
              description="Sign in once and access every organization, application module and customer service relationship linked to your account."
              href="/customer-login"
              iconName="ShoppingBagIcon"
              accentColor="info"
              features={['One global identity', 'Organization access', 'Customer modules', 'Support & profile']}
            />
          </div>

          <div className="bg-muted/60 border border-border rounded-xl px-5 py-4">
            <div className="flex items-start gap-3">
              <Shield size={16} className="text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-[12px] font-600 text-foreground mb-1">Authentication managed by AAC Control</p>
                <p className="text-[12px] text-muted-foreground leading-relaxed">
                  Owners, employees and organization administrators remain organization-scoped. Customer authentication is global and organization access is resolved from verified customer relationships.
                </p>
              </div>
            </div>
          </div>

          <p className="text-center text-[13px] text-muted-foreground mt-6">
            New to API App Cloud?{' '}
            <Link href="/platform-login-selector/register" className="text-primary font-600 hover:underline">
              Create a Client account
            </Link>
          </p>
        </div>
      </main>

      <LoginFooter year={year} />
    </div>
  );
}
